import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Zap, Leaf, Target, Cpu, UtensilsCrossed, Building2, Truck,
  Settings, ChefHat, ClipboardList, CheckCircle2,
} from "lucide-react";
import PageHeader from "../components/PageHeader";
import useReveal from "../hooks/useReveal";
import { images, navPaths, hotelClients } from "../mock/mock";
import { useLang } from "../i18n/LanguageContext";
import Seo from "../components/Seo";

const whyIcons = [Zap, Leaf, Target];
const targetIcons = [UtensilsCrossed, Building2, Truck];
const servIcons = [Settings, ChefHat, ClipboardList];

export default function Hotel() {
  const { t } = useLang();
  useReveal([t]);
  const h = t.hotel;

  return (
    <main>
      <Seo
        title="MichelChef® Hotel | Sistema PAQ per HoReCa e Hotellerie"
        description="Il sistema PAQ ottimizza la cucina di hotel, bar e ristoranti: efficienza e qualità gourmet senza chef in sala e senza aumentare i costi."
        path="/hotel"
        image={images.chefsKitchen}
      />
      <PageHeader crumb={h.crumb} eyebrow={h.eyebrow} title={h.title} subtitle={h.subtitle} image={images.chefsKitchen} homeLabel={t.common.home} />

      {/* INTRO */}
      <section className="py-24 max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div className="reveal">
            <span className="inline-flex items-center gap-2 rounded-full bg-[#17784A]/10 text-[#17784A] px-4 py-1.5 text-sm font-semibold"><Cpu size={15} /> PAQ System</span>
            <h2 className="font-display font-extrabold text-3xl lg:text-4xl mt-4 text-[#14110F]">{h.introTitle}</h2>
            <p className="mt-6 text-[#3B342E]/90 text-lg leading-relaxed">{h.introText}</p>
          </div>
          <div className="reveal reveal-delay-1">
            <img src={images.labFactory} alt="" loading="lazy" decoding="async" className="rounded-3xl w-full h-[420px] object-cover shadow-2xl" />
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="bg-[#17784A] py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="reveal text-center max-w-2xl mx-auto">
            <h2 className="font-display font-extrabold text-3xl lg:text-4xl text-white">{h.whyTitle}</h2>
          </div>
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {h.why.map((w, i) => {
              const Icon = whyIcons[i];
              return (
                <div key={i} className={`reveal reveal-delay-${i} bg-white/10 backdrop-blur rounded-2xl p-8 border border-white/10`}>
                  <span className="grid place-items-center w-14 h-14 rounded-2xl bg-white/15 text-white"><Icon size={26} /></span>
                  <h3 className="font-display font-bold text-xl mt-5 text-white">{w.t}</h3>
                  <p className="mt-2 text-white/75 leading-relaxed">{w.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PAQ SYSTEMS */}
      <section className="py-24 max-w-7xl mx-auto px-5 lg:px-8">
        <div className="reveal max-w-2xl">
          <span className="text-[#D8382E] font-semibold text-sm uppercase tracking-wider">Tech</span>
          <h2 className="font-display font-extrabold text-3xl lg:text-4xl mt-3 text-[#14110F]">{h.sysTitle}</h2>
          <p className="mt-4 text-[#3B342E]/80 text-lg">{h.sysText}</p>
        </div>
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {h.systems.map((s, i) => (
            <div key={i} className={`reveal reveal-delay-${i} group relative overflow-hidden rounded-2xl bg-[#14110F] text-white p-8 card-lift`}>
              <span className="font-display font-extrabold text-3xl text-[#22B573]">{s.name}</span>
              <p className="mt-3 text-white/70 leading-relaxed">{s.desc}</p>
              <Cpu size={120} className="absolute -right-6 -bottom-6 text-white/5 group-hover:text-[#22B573]/10 transition-colors" />
            </div>
          ))}
        </div>
      </section>

      {/* TARGETS */}
      <section className="bg-[#F3EFE7] py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="reveal text-center max-w-2xl mx-auto">
            <h2 className="font-display font-extrabold text-3xl lg:text-4xl text-[#14110F]">{h.targetTitle}</h2>
          </div>
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {h.targets.map((tg, i) => {
              const Icon = targetIcons[i];
              return (
                <div key={i} className={`reveal reveal-delay-${i} bg-white rounded-2xl p-8 shadow-sm border border-black/5 card-lift text-center`}>
                  <span className="grid place-items-center w-16 h-16 rounded-2xl bg-[#17784A]/10 text-[#17784A] mx-auto"><Icon size={30} /></span>
                  <h3 className="font-display font-extrabold text-xl mt-5 text-[#14110F]">{tg.name}</h3>
                  <p className="mt-2 text-[#3B342E]/70 leading-relaxed">{tg.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-24 max-w-7xl mx-auto px-5 lg:px-8">
        <div className="reveal max-w-2xl">
          <span className="text-[#17784A] font-semibold text-sm uppercase tracking-wider">Services</span>
          <h2 className="font-display font-extrabold text-3xl lg:text-4xl mt-3 text-[#14110F]">{h.servTitle}</h2>
          <p className="mt-4 text-[#3B342E]/80 text-lg">{h.servText}</p>
        </div>
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {h.services.map((s, i) => {
            const Icon = servIcons[i];
            return (
              <div key={i} className={`reveal reveal-delay-${i} bg-white rounded-2xl p-8 shadow-sm border border-black/5 card-lift hover:shadow-xl`}>
                <span className="grid place-items-center w-14 h-14 rounded-2xl bg-[#17784A] text-white"><Icon size={26} /></span>
                <h3 className="font-display font-bold text-xl mt-5 text-[#14110F]">{s.t}</h3>
                <p className="mt-2 text-[#3B342E]/75 leading-relaxed">{s.d}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ADVANTAGES */}
      <section className="bg-[#F3EFE7] py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="reveal max-w-2xl">
            <span className="text-[#D8382E] font-semibold text-sm uppercase tracking-wider">Vantaggi</span>
            <h2 className="font-display font-extrabold text-3xl lg:text-4xl mt-3 text-[#14110F]">{h.advTitle}</h2>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {h.advantages.map((a, i) => (
              <div key={i} className={`reveal reveal-delay-${i} bg-white rounded-2xl p-6 shadow-sm border border-black/5 card-lift`}>
                <span className="font-display font-extrabold text-2xl text-[#22B573]">{a.n}</span>
                <h3 className="font-display font-bold text-base mt-3 text-[#14110F] flex items-center gap-1.5"><CheckCircle2 size={16} className="text-[#17784A]" /> {a.t}</h3>
                <p className="mt-2 text-[#3B342E]/70 text-sm leading-relaxed">{a.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CLIENTS */}
      <section className="py-20 max-w-7xl mx-auto px-5 lg:px-8">
        <div className="reveal text-center">
          <h2 className="font-display font-extrabold text-2xl lg:text-3xl text-[#14110F]">{h.clientsTitle}</h2>
        </div>
        <div className="reveal mt-12 flex flex-wrap items-center justify-center gap-x-14 gap-y-10">
          {hotelClients.map((c, i) => (
            <img
              key={i}
              src={c.img}
              alt={c.name}
              title={c.name}
              className="h-16 w-auto object-contain grayscale opacity-60 hover:grayscale-0 hover:opacity-100 transition duration-300"
            />
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 max-w-7xl mx-auto px-5 lg:px-8">
        <div className="reveal relative overflow-hidden rounded-[32px] bg-[#17784A] px-8 py-16 lg:p-20 grain">
          <div className="relative max-w-2xl">
            <h2 className="font-display font-extrabold text-white text-3xl lg:text-5xl tracking-tight">{h.ctaTitle}</h2>
            <p className="mt-5 text-white/80 text-lg">{h.ctaText}</p>
            <Link to={navPaths.contact} className="btn-press mt-8 inline-flex items-center gap-2 rounded-full bg-white text-[#14110F] px-7 py-3.5 font-semibold hover:bg-[#FBF9F4]">
              {h.ctaBtn} <ArrowRight size={18} />
            </Link>
          </div>
          <Cpu size={280} className="absolute -right-10 -bottom-16 text-white/5 slowspin" />
        </div>
      </section>
    </main>
  );
}
